/**
 * Agent Management Panel
 * Allows support admins to view agents, edit their display name,
 * assign supervisors, and soft-delete agents with task/contact/
 * escalation/supervisor-link reassignment.
 *
 * Displayed within the Support Tasks page; the page hides the
 * "Agent Management" entry point from users without admin role.
 * As a second layer of defense the panel also gates the
 * edit/delete buttons via the `isSupportAdmin` prop. Server-side
 * RLS on support_agents enforces the same role check, and the
 * soft_delete_support_agent_and_reassign RPC re-checks via
 * _support_task_reassign_role_check().
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllAgentsWithSupervisors,
  getAllAgentsIncludingInactiveWithSupervisors,
  updateAgentSupervisor,
  updateAgentName,
  getPotentialSupervisors,
  cleanupStrandedInactiveAgent,
  getAgentAssignedContactCount,
  reactivateSupportAgent,
  describeReactivateAgentError,
} from "../../services/supportTasksDb.service";
import { useToast } from "../ui/Toast";
import type { UUID } from "../../types/common.types";
import DeleteAgentModal from "./DeleteAgentModal";

interface AgentManagementPanelProps {
  /**
   * When provided, an X close button renders in the panel header
   * (used when the panel is embedded inside SupportTasks as an
   * overlay). When omitted (e.g., on the standalone /admin/agents
   * route) the X is hidden — the page itself owns navigation back.
   */
  onClose?: () => void;
  isSupportAdmin: boolean;
  /**
   * Open straight to this agent's detail card instead of the agent list.
   * Set when the panel is opened by clicking a name on the dashboard's Agent
   * Activity table, so the operator skips "Manage Agents" -> find the row ->
   * click. Null/undefined keeps the original list-first behaviour, which is
   * still what the "Manage Agents" button wants.
   */
  initialAgentId?: UUID | null;
}

type EditMode = "name" | "supervisor" | null;

const AgentManagementPanel: React.FC<AgentManagementPanelProps> = ({
  onClose,
  isSupportAdmin,
  initialAgentId,
}) => {
  const queryClient = useQueryClient();
  const [editingAgentId, setEditingAgentId] = useState<UUID | null>(null);
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<string>("");
  const [editNameValue, setEditNameValue] = useState<string>("");
  const [deleteTargetAgent, setDeleteTargetAgent] = useState<{
    id: UUID;
    name: string;
  } | null>(null);
  // 2026-06-04: "Show inactive" toggle (audit-AI GREEN). When ON,
  // the panel switches to the include-inactive query that surfaces
  // soft-deleted + directly-deactivated agents. Inactive rows display
  // read-only with a "Cleanup needed" badge when they still have
  // stranded open tasks / contacts pointing at them.
  const [showInactive, setShowInactive] = useState(false);
  const [cleanupAgent, setCleanupAgent] = useState<{
    id: UUID;
    name: string;
  } | null>(null);
  const [cleanupTargetId, setCleanupTargetId] = useState<string>("");
  const [cleanupError, setCleanupError] = useState<string | null>(null);
  // 2026-10-26: "Reactivate" for an inactive agent (confirm dialog first).
  const [reactivateAgent, setReactivateAgent] = useState<{
    id: UUID;
    name: string;
  } | null>(null);
  const { showToast } = useToast();
  // Clicking an agent name opens a read-only detail card in place of the table.
  // Kept as local state rather than a route so the panel keeps working both as
  // the SupportTasks overlay and on the standalone /admin/agents page.
  // Seeded from initialAgentId so a dashboard click lands directly on the detail
  // card. Plain useState, not an effect syncing to the prop: the panel is mounted
  // fresh each time it opens (SupportTasks renders it behind a boolean), so the
  // initial value is always current, and an effect would fight the user's own
  // Back-to-list click by re-selecting the agent.
  const [detailAgentId, setDetailAgentId] = useState<UUID | null>(
    initialAgentId ?? null,
  );

  // Active-only branch (existing query) vs include-inactive branch.
  const { data: activeAgents = [], isLoading: agentsLoading } = useQuery({
    queryKey: ["agents-with-supervisors"],
    queryFn: getAllAgentsWithSupervisors,
    enabled: !showInactive,
  });
  const { data: allAgents = [], isLoading: allAgentsLoading } = useQuery({
    queryKey: ["agents-with-supervisors-all"],
    queryFn: getAllAgentsIncludingInactiveWithSupervisors,
    enabled: showInactive,
  });
  // Normalize to one list. Active branch lacks is_active +
  // has_stranded_refs — fold defaults so render code can share.
  const agents = showInactive
    ? allAgents
    : activeAgents.map((a) => ({
        ...a,
        is_active: true,
        has_stranded_refs: false,
        is_deleted_or_merged: false,
      }));

  const { data: supervisors = [] } = useQuery({
    queryKey: ["potential-supervisors"],
    queryFn: getPotentialSupervisors,
  });

  // The agent whose detail card is open, resolved from the already-fetched list
  // so opening the card costs no extra round-trip for the agent's own fields.
  const detailAgent = detailAgentId
    ? (agents.find((a) => a.id === detailAgentId) ?? null)
    : null;

  const {
    data: detailContactCount,
    isLoading: detailCountLoading,
    isError: detailCountError,
  } = useQuery({
    queryKey: ["agent-assigned-contact-count", detailAgentId],
    queryFn: () => getAgentAssignedContactCount(detailAgentId as UUID),
    enabled: !!detailAgentId,
  });

  const invalidateAgentQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["agents-with-supervisors"] });
    queryClient.invalidateQueries({
      queryKey: ["agents-with-supervisors-all"],
    });
    queryClient.invalidateQueries({ queryKey: ["support-agents"] });
    queryClient.invalidateQueries({ queryKey: ["support-agent-list"] });
    queryClient.invalidateQueries({ queryKey: ["agent-tasks"] });
  };

  const cleanupMutation = useMutation({
    mutationFn: ({ agentId, targetId }: { agentId: UUID; targetId: UUID }) =>
      cleanupStrandedInactiveAgent(agentId, targetId),
    onSuccess: () => {
      invalidateAgentQueries();
      setCleanupAgent(null);
      setCleanupTargetId("");
      setCleanupError(null);
    },
    onError: (err: Error) => {
      setCleanupError(err.message);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (agentId: UUID) => reactivateSupportAgent(agentId),
    onSuccess: (result) => {
      invalidateAgentQueries();
      setReactivateAgent(null);
      showToast(
        "success",
        `${result?.agent_name ?? "Agent"} is active again and will start receiving new tasks.`,
      );
    },
    onError: (err: Error) => {
      // Refreshing shows the row's real state if it changed underneath us.
      invalidateAgentQueries();
      setReactivateAgent(null);
      showToast("error", describeReactivateAgentError(err.message), 8000);
    },
  });

  const updateSupervisorMutation = useMutation({
    mutationFn: ({
      agentId,
      supervisorId,
    }: {
      agentId: UUID;
      supervisorId: UUID | null;
    }) => updateAgentSupervisor(agentId, supervisorId),
    onSuccess: () => {
      invalidateAgentQueries();
      resetEditing();
    },
  });

  const updateNameMutation = useMutation({
    mutationFn: ({ agentId, name }: { agentId: UUID; name: string }) =>
      updateAgentName(agentId, name),
    onSuccess: () => {
      invalidateAgentQueries();
      resetEditing();
    },
  });

  const resetEditing = () => {
    setEditingAgentId(null);
    setEditMode(null);
    setSelectedSupervisorId("");
    setEditNameValue("");
  };

  const handleEditNameClick = (agentId: UUID, currentName: string) => {
    setEditingAgentId(agentId);
    setEditMode("name");
    setEditNameValue(currentName);
  };

  const handleEditSupervisorClick = (
    agentId: UUID,
    currentSupervisorId: UUID | null,
  ) => {
    setEditingAgentId(agentId);
    setEditMode("supervisor");
    setSelectedSupervisorId(currentSupervisorId || "");
  };

  const handleSaveName = (agentId: UUID) => {
    if (!editNameValue.trim()) {
      return;
    }
    updateNameMutation.mutate({ agentId, name: editNameValue });
  };

  const handleSaveSupervisor = (agentId: UUID) => {
    updateSupervisorMutation.mutate({
      agentId,
      supervisorId: selectedSupervisorId || null,
    });
  };

  const handleDeleteClick = (agentId: UUID, agentName: string) => {
    setDeleteTargetAgent({ id: agentId, name: agentName });
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Agent Management
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Edit agent names, assign supervisors, and reassign tasks when
            deactivating an agent.
          </p>
          {isSupportAdmin && (
            <Link
              to="/admin/invitations"
              className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1 mt-1.5"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              View pending invitations →
            </Link>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isSupportAdmin && (
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Show inactive
            </label>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {agentsLoading || allAgentsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : agents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No active agents found.
          </div>
        ) : detailAgent ? (
          /* ---------------- Agent detail card ---------------- */
          <div>
            <button
              onClick={() => setDetailAgentId(null)}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to agents
            </button>

            <div className="border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <h4 className="text-xl font-semibold text-gray-900">
                  {detailAgent.name}
                </h4>
                {!detailAgent.is_active && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                    Inactive
                  </span>
                )}
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Email</dt>
                  <dd className="text-gray-900 mt-0.5 break-all">
                    {detailAgent.email || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Supervisor</dt>
                  <dd className="text-gray-900 mt-0.5">
                    {detailAgent.supervisor?.name || "None"}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Status</dt>
                  <dd className="text-gray-900 mt-0.5">
                    {detailAgent.is_active ? "Active" : "Inactive"}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Assigned contacts</dt>
                  <dd className="text-gray-900 mt-0.5">
                    {detailCountLoading
                      ? "Loading…"
                      : detailCountError
                        ? "Unavailable"
                        : (detailContactCount ?? 0).toLocaleString()}
                  </dd>
                </div>
              </dl>

              {/* Link into the contacts table, pre-filtered to this agent.
                  `from=agent` tells ContactList to render a Back link so the
                  operator can return here rather than losing their place. */}
              <div className="mt-5 pt-4 border-t border-gray-200">
                <Link
                  to={`/contacts?agent=${detailAgent.id}&from=agent`}
                  className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-700 font-medium text-sm"
                >
                  View contacts assigned to {detailAgent.name}
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Supervisor
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {agents.map((agent) => {
                  const isEditingThisRow = editingAgentId === agent.id;
                  const isEditingName = isEditingThisRow && editMode === "name";
                  const isEditingSupervisor =
                    isEditingThisRow && editMode === "supervisor";
                  return (
                    <tr key={agent.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isEditingName ? (
                          <input
                            type="text"
                            value={editNameValue}
                            onChange={(e) => setEditNameValue(e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            autoFocus
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setDetailAgentId(agent.id)}
                              className="font-medium text-primary-600 hover:text-primary-700 hover:underline text-left"
                            >
                              {agent.name}
                            </button>
                            {!agent.is_active && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                Inactive
                              </span>
                            )}
                            {agent.has_stranded_refs && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                Cleanup needed
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {agent.email}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isEditingSupervisor ? (
                          <select
                            value={selectedSupervisorId}
                            onChange={(e) =>
                              setSelectedSupervisorId(e.target.value)
                            }
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="">No Supervisor</option>
                            {supervisors
                              .filter((s) => s.id !== agent.id)
                              .map((supervisor) => (
                                <option
                                  key={supervisor.id}
                                  value={supervisor.id}
                                >
                                  {supervisor.name}
                                </option>
                              ))}
                          </select>
                        ) : agent.supervisor ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {agent.supervisor.name}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400 italic">
                            Not assigned
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                        {isEditingName ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleSaveName(agent.id)}
                              disabled={
                                updateNameMutation.isPending ||
                                !editNameValue.trim()
                              }
                              className="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
                            >
                              {updateNameMutation.isPending
                                ? "Saving..."
                                : "Save"}
                            </button>
                            <button
                              onClick={resetEditing}
                              className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : isEditingSupervisor ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleSaveSupervisor(agent.id)}
                              disabled={updateSupervisorMutation.isPending}
                              className="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
                            >
                              {updateSupervisorMutation.isPending
                                ? "Saving..."
                                : "Save"}
                            </button>
                            <button
                              onClick={resetEditing}
                              className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : isSupportAdmin ? (
                          agent.is_active ? (
                            <div className="flex items-center justify-end gap-3">
                              <button
                                onClick={() =>
                                  handleEditNameClick(agent.id, agent.name)
                                }
                                className="text-primary-600 hover:text-primary-700 font-medium"
                              >
                                Edit Name
                              </button>
                              <button
                                onClick={() =>
                                  handleEditSupervisorClick(
                                    agent.id,
                                    agent.supervisor_id,
                                  )
                                }
                                className="text-primary-600 hover:text-primary-700 font-medium"
                              >
                                Edit Supervisor
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteClick(agent.id, agent.name)
                                }
                                className="text-red-600 hover:text-red-700 font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-3">
                              {agent.is_deleted_or_merged ? (
                                <span
                                  className="text-xs text-gray-400 italic"
                                  title="This agent was deleted or merged into another agent and their work was moved, so they can't be reactivated."
                                >
                                  Deleted — can't reactivate
                                </span>
                              ) : (
                                <button
                                  onClick={() =>
                                    setReactivateAgent({
                                      id: agent.id,
                                      name: agent.name,
                                    })
                                  }
                                  className="text-green-700 hover:text-green-800 font-medium"
                                >
                                  Reactivate
                                </button>
                              )}
                              {agent.has_stranded_refs ? (
                                <button
                                  onClick={() => {
                                    setCleanupAgent({
                                      id: agent.id,
                                      name: agent.name,
                                    });
                                    setCleanupTargetId("");
                                    setCleanupError(null);
                                  }}
                                  className="text-amber-700 hover:text-amber-800 font-medium"
                                >
                                  Reassign + cleanup
                                </button>
                              ) : (
                                <span className="text-xs text-gray-400 italic">
                                  No open work — clean
                                </span>
                              )}
                            </div>
                          )
                        ) : (
                          <span className="text-xs text-gray-400 italic">
                            Read-only
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <div className="flex gap-3">
            <svg
              className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">About Agents</p>
              <p>
                Deleting an agent deactivates them and reassigns their open
                tasks, escalations, and assigned contacts to a chosen target
                agent. Supervisor links pointing at the deleted agent are
                cleared so you can reassign supervision manually. Completed and
                cancelled tasks keep their original attribution for historical
                reporting.
              </p>
              <p className="mt-2">
                To bring back an inactive agent, turn on &ldquo;Show
                inactive&rdquo; and click Reactivate. An agent who was deleted
                or merged into another agent can&apos;t be reactivated.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteTargetAgent && (
        <DeleteAgentModal
          agent={deleteTargetAgent}
          allAgents={agents.filter((a) => a.is_active)}
          onClose={() => setDeleteTargetAgent(null)}
          onSuccess={() => {
            invalidateAgentQueries();
            setDeleteTargetAgent(null);
          }}
        />
      )}

      {/* Reactivate confirmation. The RPC re-checks everything (manager
          role, user active + support role, not deleted/merged) and the
          toast names the specific reason when it refuses. */}
      {reactivateAgent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reactivate-agent-title"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3
                id="reactivate-agent-title"
                className="text-lg font-semibold text-gray-900"
              >
                Reactivate {reactivateAgent.name}?
              </h3>
            </div>
            <div className="px-6 py-4 space-y-2 text-sm text-gray-700">
              <p>
                <span className="font-medium">{reactivateAgent.name}</span>{" "}
                becomes an active support agent again and{" "}
                <span className="font-medium">
                  starts receiving newly assigned tasks
                </span>
                and new member assignments in their region. They can also be
                picked as a reassignment target again.
              </p>
              <p className="text-gray-500">
                Tasks and contacts that were moved to other agents while they
                were inactive stay where they are. Their CRM user must be active
                and have the Support Agent or Support Admin role.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 rounded-b-lg">
              <button
                type="button"
                onClick={() => setReactivateAgent(null)}
                disabled={reactivateMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => reactivateMutation.mutate(reactivateAgent.id)}
                disabled={reactivateMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {reactivateMutation.isPending
                  ? "Reactivating..."
                  : "Reactivate agent"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cleanup-stranded-inactive-agent modal. Mounted parallel to
          the soft-delete modal but distinct flow: source agent is
          ALREADY inactive (this is a retroactive cleanup), so the
          UI explicitly names that. */}
      {cleanupAgent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Reassign + cleanup inactive agent
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                <span className="font-medium">{cleanupAgent.name}</span> was
                deactivated without going through the standard delete flow.
                Choose an active agent to take their open tasks, escalations,
                and contact assignments.
              </p>
            </div>
            <div className="px-6 py-4 space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Reassign open work to
              </label>
              <select
                value={cleanupTargetId}
                onChange={(e) => setCleanupTargetId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Pick a target...</option>
                {agents
                  .filter((a) => a.is_active && a.id !== cleanupAgent.id)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.email})
                    </option>
                  ))}
              </select>
              {cleanupError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  {cleanupError}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 rounded-b-lg">
              <button
                type="button"
                onClick={() => {
                  setCleanupAgent(null);
                  setCleanupTargetId("");
                  setCleanupError(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  cleanupTargetId &&
                  cleanupMutation.mutate({
                    agentId: cleanupAgent.id,
                    targetId: cleanupTargetId as UUID,
                  })
                }
                disabled={!cleanupTargetId || cleanupMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {cleanupMutation.isPending
                  ? "Cleaning up..."
                  : "Reassign + cleanup"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentManagementPanel;
